import { useApolloClient } from '@apollo/client'
import produce from 'immer'
import { v1 as uuid } from 'uuid'

import {
  AnnotationCommentCountFragment,
  AnnotationCommentCountFragmentDoc,
  GetAnnotationCommentsQueryVariables,
  GetFrameAnnotationInfoDocument,
  GetFrameAnnotationInfoQuery,
  GetFrameAnnotationInfoQueryVariables,
  useCreateAnnotationCommentMutation,
} from '@sketch/gql-types'

import { dataIdFromObject } from '@sketch/graphql-cache'
import { errorPreventiveFragmentRead } from '../../utils/apolloCache'

import { updateCommentsQuery } from '../utils/cache'

import {
  createCommentFragment,
  createInitialUserQuery,
  hasOptimisticResponseVariables,
} from '../utils'

const useCreateAnnotationComment = (
  annotationIdentifier: string,
  /**
   * The variables the comments list is being read with. Apollo keys the
   * `comments` field by its arguments, so the centred preview (which reads with
   * `related`/`limit`) stores a *different* field value than the default
   * full-thread view. We must update the cache with the same variables the list
   * uses, otherwise the new reply is written to the wrong entry and never shows
   * up. Defaults to the plain full-thread variables.
   */
  commentsQueryVariables: GetAnnotationCommentsQueryVariables = {
    annotationIdentifier,
  }
) => {
  const apollo = useApolloClient()
  const [createComment] = useCreateAnnotationCommentMutation({
    onCompleted: data => {
      const frameUUID =
        data?.createAnnotationReply?.comment.annotation.frameUuid
      const shareIdentifier =
        data?.createAnnotationReply?.comment.annotation.share.identifier
      if (frameUUID && shareIdentifier) {
        apollo.query<
          GetFrameAnnotationInfoQuery,
          GetFrameAnnotationInfoQueryVariables
        >({
          query: GetFrameAnnotationInfoDocument,
          variables: {
            frameUUID,
            shareIdentifier,
          },
        })
      }
    },
    onError: 'show-toast',
  })

  return (...args: Parameters<typeof createComment>) => {
    const commentIdentifier = uuid()

    return createComment({
      ...args[0],
      update: (cache, { data }) => {
        /**
         *  1st step update the annotation query
         */
        const comment = data?.createAnnotationReply.comment

        if (!comment) {
          // If there's no comment response no need to update anything then
          return
        }

        updateCommentsQuery(cache, commentsQueryVariables, data => {
          if (!data?.annotation) {
            // Preventive measure
            return
          }

          const { comments } = data.annotation

          /**
           * In the centred preview (keyed by `related`) the list shows a fixed
           * window, not the full thread. When that window doesn't reach the
           * newest end — the `before` cursor points at more recent comments we
           * haven't loaded — appending this reply would place it directly after
           * the window, leaving a gap where those unloaded newer comments belong
           * and producing a misleading list. Skip it: the reply still lives on
           * the server, and the "see the most recent comment" link navigates to
           * the full, live thread. Only relevant in centred mode; the regular
           * full-thread view is always anchored to the newest comment.
           */
          if (commentsQueryVariables.related && comments.meta.before) {
            return
          }

          /**
           * Prevent having 2 annotations with the
           * same identifier added
           */
          const commentAlreadyExists = comments.entries.find(
            ({ identifier }) => comment.identifier === identifier
          )

          if (commentAlreadyExists) {
            return
          }

          // If we reach the end of the list and there's no comment updating
          // means this is the optimistic comment, let's add it then
          comments.entries.unshift(comment)
          comments.meta.totalCount = comments.meta.totalCount + 1
        })

        /**
         * 2nd step update the annotation reply count if loaded
         */
        const annotationCacheId = dataIdFromObject({
          __typename: 'Annotation',
          identifier: annotationIdentifier,
        })!

        // Read the annotation pagination
        const annotationPagination =
          errorPreventiveFragmentRead<AnnotationCommentCountFragment>(apollo, {
            fragment: AnnotationCommentCountFragmentDoc,
            id: annotationCacheId,
          })

        if (!annotationPagination) {
          // If the fragment doesn't exist we don't update :)
          return
        }

        const updatePagination = produce(
          annotationPagination,
          ({ comments }) => {
            comments.meta.totalCount = comments.meta.totalCount + 1
          }
        )

        cache.writeFragment({
          fragment: AnnotationCommentCountFragmentDoc,
          id: annotationCacheId,
          data: updatePagination,
        })
      },
      optimisticResponse: variables => {
        if (!hasOptimisticResponseVariables(variables)) {
          throw new Error(
            'Missing variables in optimistic update (`createAnnotationComment` mutation)'
          )
        }

        const { body, identifier } = variables

        const user = createInitialUserQuery(apollo)

        const comment = createCommentFragment(body, user.me, commentIdentifier)

        return {
          __typename: 'RootMutationType',
          createAnnotationReply: {
            __typename: 'CreateAnnotationReplyResponse',
            comment: {
              ...comment,
              annotation: {
                __typename: 'Annotation',
                subscriptionStatus: 'ON',
                latestCommentCreatedAt: comment.createdAt,
                identifier,

                /* Provide complete Page structure to satisfy Apollo cache requirements */
                page: {
                  __typename: 'Page',
                  identifier: uuid(),
                  uuid: uuid(),
                  pageUUID: uuid(),
                  hasUnreadComments: false,
                },
                frameUuid: 'some-fake-frame-uuid',
                share: {
                  __typename: 'Share',
                  identifier: 'some-fake-identifier',
                  subscriptionStatus: 'PARTIAL',
                },
              },
            },
          },
        }
      },
    })
  }
}

export default useCreateAnnotationComment
